import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { 
  LayoutDashboard, 
  Bed, 
  Swords, 
  Cog, 
  Sparkles, 
  History, 
  Menu, 
  LogOut,
  ChevronLeft,
  Hotel,
  Link2,
  ClipboardEdit,
  Bell,
  CalendarDays,
  BarChart3,
  PartyPopper,
  Mail,
  NotebookPen,
  TrendingUp,
  Activity,
  Tv2,
  SlidersHorizontal,
  Tag,
  Wrench
} from "lucide-react";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/changelog", label: "Registro Cambios", icon: NotebookPen },
  { path: "/calendar", label: "Calendario", icon: CalendarDays },
  { path: "/comparison", label: "Comparativa Anual", icon: BarChart3 },
  { path: "/pickup", label: "Pickup & Pace", icon: Activity },
  { path: "/forecast", label: "Previsión & Simulador", icon: TrendingUp },
  { path: "/oscar-rules", label: "Reglas Oscar", icon: SlidersHorizontal },
  { path: "/precios", label: "Laboratorio Precios", icon: Tag },
  { path: "/tarifas-futuras", label: "Tarifas 2027/28", icon: TrendingUp },
  { path: "/tv", label: "Modo TV", icon: Tv2 },
  { path: "/alerts", label: "Alertas", icon: Bell },
  { path: "/quick-occupancy", label: "Ocupación Rápida", icon: ClipboardEdit },
  { path: "/rooms", label: "Habitaciones", icon: Bed },
  { path: "/competitors", label: "Competencia", icon: Swords },
  { path: "/revenue-engine", label: "Motor de Precios", icon: Cog },
  { path: "/ai-analysis", label: "Análisis IA", icon: Sparkles },
  { path: "/events", label: "Eventos", icon: PartyPopper },
  { path: "/contacts", label: "Contactos MICE", icon: Mail },
  { path: "/price-history", label: "Historial", icon: History },
  { path: "/redforts", label: "Redforts Oscar", icon: Link2 },
  { path: "/admin", label: "Administración", icon: Wrench },
];

const NavItem = ({ item, isActive, collapsed }) => (
  <Link
    to={item.path}
    data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
    className={`nav-item ${isActive ? "nav-item-active" : ""} ${collapsed ? "justify-center px-2" : ""}`}
  >
    <item.icon className="w-5 h-5 flex-shrink-0" />
    {!collapsed && <span>{item.label}</span>}
  </Link>
);

const Sidebar = ({ collapsed, onCollapse }) => {
  const location = useLocation();
  const { user, logout } = useAuth();

  return (
    <aside className={`sidebar ${collapsed ? "sidebar-collapsed hidden md:flex" : ""} flex-col`}>
      <div className="p-4 border-b border-border">
        <div className={`flex items-center ${collapsed ? "justify-center" : "gap-3"}`}>
          <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
            <Hotel className="w-6 h-6 text-primary-foreground" />
          </div>
          {!collapsed && (
            <div>
              <h1 className="font-semibold text-lg">Revenue Hub</h1>
              <p className="text-xs text-muted-foreground">Port Sitges Hotel</p>
            </div>
          )}
        </div>
      </div>

      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => (
          <NavItem
            key={item.path}
            item={item}
            isActive={location.pathname === item.path}
            collapsed={collapsed}
          />
        ))}
        <button
          data-testid="sidebar-open-oscar-btn"
          onClick={() => window.open("https://oscar.redforts.com/AHCnLzvj/dashboard", "_blank", "noopener,noreferrer")}
          className={`mt-3 w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium border-2 border-dashed border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors ${collapsed ? "justify-center" : ""}`}
          title="Abrir Oscar PMS en otra pestaña"
        >
          <Link2 className="w-5 h-5 flex-shrink-0" />
          {!collapsed && (
            <span className="flex-1 text-left">
              Abrir Oscar
            </span>
          )}
        </button>
      </nav>

      <div className="p-4 border-t border-border">
        {!collapsed && (
          <div className="mb-3 px-3">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
        )}
        <Button
          variant="ghost"
          className={`w-full ${collapsed ? "px-2" : ""}`}
          onClick={logout}
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span className="ml-2">Cerrar Sesión</span>}
        </Button>
      </div>

      <Button
        variant="ghost"
        size="icon"
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-card border border-border shadow-sm hidden md:flex"
        onClick={() => onCollapse(!collapsed)}
        data-testid="collapse-sidebar-btn"
      >
        <ChevronLeft className={`w-4 h-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
      </Button>
    </aside>
  );
};

const MobileNav = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  // Close sheet when route changes
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-btn">
          <Menu className="w-6 h-6" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-64 p-0">
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
              <Hotel className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="font-semibold text-lg">Revenue Hub</h1>
              <p className="text-xs text-muted-foreground">Port Sitges Hotel</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <NavItem
              key={item.path}
              item={item}
              isActive={location.pathname === item.path}
              collapsed={false}
            />
          ))}
          <button
            data-testid="mobile-open-oscar-btn"
            onClick={() => window.open("https://oscar.redforts.com/AHCnLzvj/dashboard", "_blank", "noopener,noreferrer")}
            className="mt-3 w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium border-2 border-dashed border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
          >
            <Link2 className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1 text-left">Abrir Oscar</span>
          </button>
        </nav>

        <div className="p-4 border-t border-border">
          <div className="mb-3 px-3">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <Button variant="ghost" className="w-full" onClick={logout}>
            <LogOut className="w-5 h-5 mr-2" />
            Cerrar Sesión
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

const Layout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar collapsed={collapsed} onCollapse={setCollapsed} />
      
      <div className={`main-content ${collapsed ? "main-content-expanded" : ""}`}>
        <header className="sticky top-0 z-30 glass border-b border-border px-4 py-3 md:px-6">
          <div className="flex items-center justify-between">
            <MobileNav />
            <div className="flex-1" />
          </div>
        </header>
        
        <main className="p-4 md:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
